import { FC } from 'react';
import { Color as SunshineColor, getColorValue } from '@shinetools/sunshine';
import { format } from 'date-fns';

import { formatAmount } from 'common/formatAmount';
import { Format, formatDate, FORMATS, FormatString } from 'helpers/date';

import {
  DashboardGranularity,
  DashboardPaymentMethod,
} from '../../../__generated__/GQL';
import capitalizeFirstLetter from '../../../helpers/string/capitalizeFirstLetter';
import {
  ClientEmptyState,
  TeamEmptyState,
  TransactionsEmptyState,
} from '../pages/components/TransactionAggregationTable/components/EmptyStates';
import { StatIndicatorMode } from '../pages/components/TransactionAggregationTable/components/TransactionAggregationItem/components/StatIndicator';
import { TransactionAggregationItemWithId } from '../pages/components/TransactionAggregationTable/table';
import { GraphType } from '../pages/Overview/components/GraphSelector';
import { DashboardFilter } from '../types';
import dashboardLocales from './locales';

export const DASHBOARD_FILTER_TO_STAT_MODE: Record<
  DashboardFilter,
  StatIndicatorMode
> = {
  [DashboardFilter.operationType]: 'delta',
  [DashboardFilter.clients]: 'simple',
  [DashboardFilter.merchant]: 'transaction-count',
  [DashboardFilter.team]: 'delta',
};

export const TRANSACTION_AGGREGATION_EMPTY_STATES: Record<DashboardFilter, FC> =
  {
    [DashboardFilter.operationType]: TransactionsEmptyState,
    [DashboardFilter.clients]: ClientEmptyState,
    [DashboardFilter.merchant]: TransactionsEmptyState,
    [DashboardFilter.team]: TeamEmptyState,
  };

export const activeDot = (dotColor: SunshineColor) => {
  return {
    fill: 'white',
    r: 7,
    stroke: getColorValue(dotColor),
    strokeWidth: 2,
  };
};

export const yAxisLabelFormatter = (value: number) =>
  formatAmount({ forcePlusSign: false, isMinorUnitValue: true, value });

export const dateTickFormatter = (
  dateString: string,
  dateFormat: Format | FormatString = FORMATS.dMMM,
) => {
  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return dateString || '';
  }

  return formatDate(date, dateFormat);
};

const dateYearFormatter = (dateString: string) => {
  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return dateString || '';
  }

  return formatDate(date, FORMATS.yyyy);
};

export const formatUTCStartOfDay = (date: Date) =>
  `${format(date, 'yyyy-MM-dd')}T00:00:00.000Z`;
export const formatUTCEndOfDay = (date: Date) =>
  `${format(date, 'yyyy-MM-dd')}T23:59:59.999Z`;

export const xAxisDateFormatter = (
  dateString: string | null,
  granularity: DashboardGranularity,
) => {
  if (!dateString) {
    return '';
  }

  if (granularity === DashboardGranularity.Month) {
    const date = new Date(dateString);
    const formattedDate = formatDate(date, FORMATS.MMM);
    // Add capitalize letter for FR as well
    return capitalizeFirstLetter(formattedDate);
  }

  return dateTickFormatter(dateString);
};

export interface TooltipRangeDate {
  day: string;
  year: string;
}

export const tooltipDateFormatter = (
  dateString: string | null,
): TooltipRangeDate => {
  if (!dateString) {
    return {
      day: '',
      year: '',
    };
  }

  const day = dateTickFormatter(dateString);
  const year = dateYearFormatter(dateString);

  return {
    day,
    year,
  };
};

export const paymentMethodItems = (
  type: GraphType,
  paymentMethod: DashboardPaymentMethod | undefined,
) => {
  if (!paymentMethod) {
    return [];
  }

  const newValue =
    type === 'payin'
      ? paymentMethod.payin.newValue
      : paymentMethod.payout.newValue;
  const oldValue =
    type === 'payin'
      ? paymentMethod.payin.oldValue
      : paymentMethod.payout.oldValue;
  const paymentMethods: TransactionAggregationItemWithId[] = [];

  const cardTransactionAggregationItem: TransactionAggregationItemWithId = {
    currentValue: newValue.card,
    delta: (newValue.card ?? 0) - (oldValue?.card ?? 0),
    icon: 'card-verso',
    id: 'card',
    title: dashboardLocales.operationType.card,
  };

  const checkTransactionAggregationItem: TransactionAggregationItemWithId = {
    currentValue: newValue.check,
    delta: (newValue.check ?? 0) - (oldValue?.check ?? 0),
    icon: 'card-verso',
    id: 'check',
    title: dashboardLocales.operationType.check,
  };

  const cashTransactionAggregationItem: TransactionAggregationItemWithId = {
    currentValue: newValue.cashDeposit,
    delta: (newValue.cashDeposit ?? 0) - (oldValue?.cashDeposit ?? 0),
    icon: 'cash',
    id: 'cash',
    title:
      type === 'payin'
        ? dashboardLocales.operationType.deposit
        : dashboardLocales.operationType.withdrawal,
  };

  const newTransfer =
    (newValue.directDebit ?? 0) +
    (newValue.instantTransfer ?? 0) +
    (newValue.swiftTransfer ?? 0) +
    (newValue.transfer ?? 0) +
    (newValue.walletTransfer ?? 0);
  const oldTransfer =
    (oldValue?.directDebit ?? 0) +
    (oldValue?.instantTransfer ?? 0) +
    (oldValue?.swiftTransfer ?? 0) +
    (oldValue?.transfer ?? 0) +
    (oldValue?.walletTransfer ?? 0);

  const transferTransactionAggregationItem: TransactionAggregationItemWithId = {
    currentValue: newTransfer,
    delta: newTransfer - oldTransfer,
    icon: 'transfer',
    id: 'transfer',
    title: dashboardLocales.operationType.transfer,
  };

  if (type === 'payin') {
    paymentMethods.push(
      checkTransactionAggregationItem,
      cashTransactionAggregationItem,
      transferTransactionAggregationItem,
      cardTransactionAggregationItem,
    );
  } else {
    paymentMethods.push(
      transferTransactionAggregationItem,
      cardTransactionAggregationItem,
      cashTransactionAggregationItem,
    );
  }

  return paymentMethods.sort(
    // descending order
    (a, b) => (b.currentValue ?? 0) - (a.currentValue ?? 0),
  );
};

export const GRAPH_ANIMATION_DURATION = 200;
export const GRAPH_ANIMATION_EASING = 'ease-in-out';
